import type { ButtonSizeType } from '@design-system';
import { isInNextSixDays } from '@utils/product';
import React, { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { DrawWithFrames } from '../../../../features/draws/types';
import {
  getProductRef,
  getProductStatus,
} from '../../../../helpers/purchasing';
import { useDrawStage } from '../../../../hooks/useDrawStage';
import { useDraws } from '../../../../providers/DrawProvider';
import { GeneratedDrawType } from '../../../../types/generated';
import type {
  ProductPurchaseDetails,
  ProductPurchasingController,
} from '../../../../types/Purchasing';
import type { ButtonLocation } from '../../../../types/Subscriptions';
import { SimpleDialog } from '../../../Dialog/SimpleDialog';
import ProductDate from '../../ProductDate';
import { DrawFlow, PreOrderFlow, PublicReleaseFlow } from '../flows';
import { AuctionFlow, getOrdinal } from '../flows/AuctionFlow';
import { DrawExplainer } from '../flows/DrawExplainer';
import { SignUpButton } from '../SignUpButton';
import { PublicSubscriptionCta } from './Public';
import { SoldOutSubscriptionCta } from './SoldOut';

const DrawUpcomingSubscriptionCta = ({
  buttonLocation,
  buttonSize,
  draw,
  product,
  purchasingController,
}: {
  buttonLocation?: ButtonLocation;
  buttonSize?: ButtonSizeType;
  draw: DrawWithFrames;
  product: ProductType;
  purchasingController: ProductPurchasingController<ProductPurchaseDetails>;
}) => {
  const { t } = useTranslation('product');
  const launchDate = new Date(draw?.draw?.openAt);
  const [showDialog, setShowDialog] = useState(false);

  const label = useMemo(() => {
    if (isInNextSixDays(launchDate)) {
      return draw?.draw?.drawType === GeneratedDrawType.Auction
        ? t('subscriptionCta.auction.startsIn')
        : t('subscriptionCta.draw.startsIn');
    }
    return draw?.draw?.drawType === GeneratedDrawType.Auction
      ? t('subscriptionCta.auction.startsOn')
      : t('subscriptionCta.draw.startsOn');
  }, [launchDate, draw?.draw?.drawType, t]);

  const auctionLearnMoreBodyLabel =
    draw?.draw?.allocation > 1
      ? 'auction.default.learnMore.RankedAuctionBody'
      : 'auction.default.learnMore.SingleAuctionBody';

  const auctionParagraphsResult = t(auctionLearnMoreBodyLabel, {
    returnObjects: true,
  });
  const auctionParagraphs = Array.isArray(auctionParagraphsResult)
    ? auctionParagraphsResult
    : [];

  return (
    <div className="flex w-full flex-1 flex-col space-y-6">
      <div className="flex w-full flex-1 flex-col">
        <p className="text-center font-bold">{label}</p>
        <p className="text-center">
          <ProductDate date={launchDate} />
        </p>
      </div>

      <div className="flex w-full flex-1 flex-col space-y-3">
        <SignUpButton
          buttonLocation={buttonLocation}
          labels={{ normal: t('notifyMe'), success: t('subscribed') }}
          product={product}
          productRef={getProductRef(purchasingController)}
          productStatus={getProductStatus(product, purchasingController)}
          showIcon
          size={buttonSize}
        />
        <button
          className="hyperlink"
          onClick={() => {
            setShowDialog(true);
            // handleHowToClick();
          }}
          type="button"
        >
          {draw?.draw?.drawType === GeneratedDrawType.Auction
            ? t('auction.default.learnMore.title')
            : t('subscriptionCta.draw.learnMoreDialog.title')}
        </button>
      </div>
      <SimpleDialog
        onClose={() => setShowDialog(false)}
        open={showDialog}
        title={t('auction.default.learnMore.blockChain.title')}
      >
        {draw?.draw?.drawType === GeneratedDrawType.Auction ? (
          <div className="flex flex-col gap-3">
            {auctionParagraphs.map((paragraph, index) => (
              <p>
                <Trans
                  key={paragraph}
                  i18nKey={`auction.default.learnMore.blockChain.body.${index}`}
                  values={{
                    size: draw?.draw?.allocation,
                    position: `${draw?.draw?.allocation}${getOrdinal(
                      draw?.draw?.allocation ?? 0,
                    )}`,
                  }}
                >
                  {paragraph}
                </Trans>
              </p>
            ))}
          </div>
        ) : (
          <DrawExplainer />
        )}
      </SimpleDialog>
    </div>
  );
};

type Props<T extends ProductPurchaseDetails> = {
  buttonLocation?: ButtonLocation;
  buttonSize?: ButtonSizeType;
  product: ProductType;
  purchasingController: ProductPurchasingController<T>;
};

export const DrawCta: React.FC<Props<ProductPurchaseDetails>> = ({
  buttonLocation,
  buttonSize,
  product,
  purchasingController,
}) => {
  const { mainDraw } = useDraws();
  const stage = useDrawStage(mainDraw?.draw);

  if (!mainDraw) {
    return (
      <PublicSubscriptionCta
        buttonLocation={buttonLocation}
        buttonSize={buttonSize}
        product={product}
        purchasingController={purchasingController}
      />
    );
  }

  switch (stage) {
    case 'UPCOMING':
      return (
        <DrawUpcomingSubscriptionCta
          buttonLocation={buttonLocation}
          buttonSize={buttonSize}
          draw={mainDraw}
          product={product}
          purchasingController={purchasingController}
        />
      );
    case 'SOLD_OUT':
      return (
        <SoldOutSubscriptionCta
          buttonLocation={buttonLocation}
          buttonSize={buttonSize}
          product={product}
          purchasingController={purchasingController}
        />
      );
    case 'DRAW':
    case 'DRAW_PRE_ORDER_CLOSED':
      return product.releaseType === 'auction' ? <AuctionFlow /> : <DrawFlow />;
    case 'DRAW_PRE_ORDER_OPEN':
    case 'FCFS_PRE_ORDER_OPEN':
      return <PreOrderFlow product={product} />;
    case 'FCFS_PRE_ORDER_CLOSED':
      return (
        <PublicReleaseFlow
          draw={mainDraw.draw}
          product={product}
          purchasingController={purchasingController}
        />
      );
    default:
      return (
        <PublicSubscriptionCta
          buttonLocation={buttonLocation}
          buttonSize={buttonSize}
          product={product}
          purchasingController={purchasingController}
        />
      );
  }
};
